import VariableService from '@/services/variables';
import {
  buildUserVariableUpdatePayload,
  type MyVariable,
  type UserVariableUpdatePayload,
} from '@/types/variables';
import {
  cityRecordToBirthPlace,
  formatCityLabel,
  searchCitiesInCatalog,
  type CityRecord,
} from '@/utils/city-catalog';
import { isAxiosError } from 'axios';

export const SOULMAP_PROFILE_DRAFT_KEY = 'soulmap-profile-draft';

/** my-variables API names. Agent prompts reference these as user.{name}. */
export const SOULMAP_PROFILE_FIELD_KEYS = [
  'language_protocol',
  'guidance_style',
  'birth_date',
  'birth_time',
  'birth_place',
  'birth_lat',
  'birth_long',
] as const;

export type SoulMapProfileFieldKey =
  (typeof SOULMAP_PROFILE_FIELD_KEYS)[number];

/**
 * Boolean user variable set once the user has confirmed their birth details.
 * Kept out of SOULMAP_PROFILE_FIELD_KEYS because it is not a form field and
 * must not count towards isSoulMapProfileConfigured.
 */
const BIRTH_CONFIRMED_VARIABLE_NAMES = [
  'is_birth_details_confirmed',
  'user.is_birth_details_confirmed',
];

const BIRTH_CONFIRMED_WRITE_NAME = 'is_birth_details_confirmed';

const HIDDEN_USER_PROFILE_VARIABLE_NAMES = new Set([
  BIRTH_CONFIRMED_WRITE_NAME,
  'birth_date',
  'birth_place',
  'birth_time',
  'birth_lat',
  'birth_long',
  'guidance_style',
  'language_protocol',
  // Bookkeeping for the "Soul Map is ready" modal, never a profile field.
  'soul_map_ready_seen',
]);

export function normalizeProfileVariableName(value: string): string {
  return value.trim().toLowerCase().replace(/^user\./, '');
}

function matchesProfileVariableName(
  variable: string | { name?: string; description?: string },
  isMatch: (normalized: string) => boolean
): boolean {
  const values =
    typeof variable === 'string'
      ? [variable]
      : [variable.name, variable.description];

  return values.some((value) => {
    if (!value) return false;
    return isMatch(normalizeProfileVariableName(value));
  });
}

export function isBirthDetailsConfirmedVariable(
  variable: string | { name?: string; description?: string }
): boolean {
  return matchesProfileVariableName(
    variable,
    (normalized) => normalized === BIRTH_CONFIRMED_WRITE_NAME
  );
}

/** System Soul Map fields that should not appear in the User Profile panel. */
export function isHiddenUserProfileVariable(
  variable: string | { name?: string; description?: string }
): boolean {
  return matchesProfileVariableName(variable, (normalized) =>
    HIDDEN_USER_PROFILE_VARIABLE_NAMES.has(normalized)
  );
}

export type LanguageProtocolPreference = 'spiritual' | 'secular';

export const LANGUAGE_PROTOCOL_OPTIONS: LanguageProtocolPreference[] = [
  'spiritual',
  'secular',
];

export type GuidanceStylePreference = 'soul-affirming' | 'neutral';

export const GUIDANCE_STYLE_OPTIONS: GuidanceStylePreference[] = [
  'soul-affirming',
  'neutral',
];

export interface SoulMapBirthPlace {
  label: string;
  placeId: string;
  lat: number;
  lng: number;
}

export interface SoulMapProfileForm {
  languageProtocol: LanguageProtocolPreference;
  guidanceStyle: GuidanceStylePreference;
  birthDate: string;
  birthTime: string;
  birthPlace: SoulMapBirthPlace | null;
}

export const EMPTY_SOULMAP_PROFILE_FORM: SoulMapProfileForm = {
  languageProtocol: 'spiritual',
  guidanceStyle: 'soul-affirming',
  birthDate: '',
  birthTime: '',
  birthPlace: null,
};

export function isLanguageProtocolPreference(
  value: unknown
): value is LanguageProtocolPreference {
  return value === 'spiritual' || value === 'secular';
}

function normalizeVariableString(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (Array.isArray(value)) return normalizeVariableString(value[0]);
  return String(value).trim();
}

export function parseLanguageProtocolPreference(
  value: unknown
): LanguageProtocolPreference | null {
  const normalized = normalizeVariableString(value).toLowerCase();
  return isLanguageProtocolPreference(normalized) ? normalized : null;
}

export function isGuidanceStylePreference(
  value: unknown
): value is GuidanceStylePreference {
  return value === 'soul-affirming' || value === 'neutral';
}

export function parseGuidanceStylePreference(
  value: unknown
): GuidanceStylePreference | null {
  const normalized = normalizeVariableString(value).toLowerCase();
  return isGuidanceStylePreference(normalized) ? normalized : null;
}

/** Normalize API / session values to `YYYY-MM-DD`. */
export function toDateInputValue(value: unknown): string {
  const raw = normalizeVariableString(value);
  if (!raw) return '';

  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    return raw;
  }

  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) {
    return '';
  }

  return parsed.toISOString().slice(0, 10);
}

/** Normalize API / session values to `HH:mm`. */
export function toTimeInputValue(value: unknown): string {
  const raw = normalizeVariableString(value);
  if (!raw) return '';

  if (/^\d{2}:\d{2}$/.test(raw)) {
    return raw;
  }

  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(raw)) {
    return raw.slice(11, 16);
  }

  const parsed = new Date(`1970-01-01T${raw}`);
  if (!Number.isNaN(parsed.getTime())) {
    return raw.slice(0, 5);
  }

  const dateParsed = new Date(raw);
  if (!Number.isNaN(dateParsed.getTime())) {
    const offsetMs = dateParsed.getTimezoneOffset() * 60_000;
    return new Date(dateParsed.getTime() - offsetMs)
      .toISOString()
      .slice(11, 16);
  }

  return '';
}

/** API / chart service format: `YYYY-MM-DD`. */
export function formatBirthDateForApi(value: string): string {
  const trimmed = value.trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    return trimmed;
  }

  return toDateInputValue(trimmed);
}

/** API / chart service format: `HH:MM` (24-hour, zero-padded). */
export function formatBirthTimeForApi(value: string): string {
  const trimmed = value.trim();
  if (/^\d{2}:\d{2}$/.test(trimmed)) {
    return trimmed;
  }

  const parts = trimmed.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
  if (parts) {
    return `${parts[1].padStart(2, '0')}:${parts[2]}`;
  }

  return toTimeInputValue(trimmed);
}

export interface BirthPlaceLabelParts {
  displayName?: string | { text?: string | null } | null;
  formattedAddress?: string | null;
  addressComponents?: Array<{
    longText?: string | null;
    shortText?: string | null;
    types?: string[];
  }> | null;
  fallback?: string;
}

/** Strip leading postal codes and normalize whitespace for display/API. */
export function normalizeBirthPlaceLabel(label: string): string {
  return label
    .replace(/^\d{4,10}\s+/, '')
    .replace(/\s+/g, ' ')
    .trim();
}

function getPlaceDisplayName(
  displayName: BirthPlaceLabelParts['displayName']
): string {
  if (!displayName) return '';
  if (typeof displayName === 'string') return displayName.trim();
  return displayName.text?.trim() ?? '';
}

function getAddressComponent(
  components: BirthPlaceLabelParts['addressComponents'],
  type: string
): string {
  const match = components?.find((component) =>
    component.types?.includes(type)
  );
  const value = match?.longText ?? match?.shortText;
  return value?.trim() ?? '';
}

/** Build a human-readable city label without postal codes. */
export function formatBirthPlaceLabel(parts: BirthPlaceLabelParts): string {
  const city =
    getPlaceDisplayName(parts.displayName) ||
    getAddressComponent(parts.addressComponents, 'locality') ||
    getAddressComponent(parts.addressComponents, 'postal_town') ||
    getAddressComponent(parts.addressComponents, 'administrative_area_level_2');

  const country = getAddressComponent(parts.addressComponents, 'country');

  if (city && country) {
    return normalizeBirthPlaceLabel(`${city}, ${country}`);
  }

  if (city) {
    return normalizeBirthPlaceLabel(city);
  }

  const formatted =
    parts.formattedAddress?.trim() || parts.fallback?.trim() || '';
  return normalizeBirthPlaceLabel(formatted);
}

export function parseSoulMapBirthPlace(
  value: unknown
): SoulMapBirthPlace | null {
  if (value === null || value === undefined) return null;

  if (typeof value === 'object' && !Array.isArray(value)) {
    const record = value as Record<string, unknown>;
    const label = normalizeVariableString(record.label);
    const placeId = normalizeVariableString(record.placeId);
    const lat = Number(record.lat);
    const lng = Number(record.lng);
    if (label && Number.isFinite(lat) && Number.isFinite(lng)) {
      return {
        label: normalizeBirthPlaceLabel(label),
        placeId,
        lat,
        lng,
      };
    }
  }

  const raw = normalizeVariableString(value);
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    return parseSoulMapBirthPlace(parsed);
  } catch {
    return null;
  }
}

export function getBirthPlaceLabelFromVariable(value: unknown): string {
  const fromStructured = parseSoulMapBirthPlace(value);
  if (fromStructured?.label) {
    return fromStructured.label;
  }

  return normalizeBirthPlaceLabel(normalizeVariableString(value));
}

function parseCoordinate(value: unknown): number | null {
  const raw = normalizeVariableString(value);
  if (!raw) return null;
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : null;
}

export function parseMyVariablesList(data: unknown): MyVariable[] {
  if (Array.isArray(data)) return data;
  if (data && typeof data === 'object' && 'results' in data) {
    const results = (data as { results?: MyVariable[] }).results;
    return Array.isArray(results) ? results : [];
  }
  return [];
}

/** Match tenant variable by API name or prompt-style user.{name}. */
export function findSoulMapProfileVariable(
  variables: MyVariable[] | undefined,
  fieldKey: SoulMapProfileFieldKey
): MyVariable | undefined {
  const aliases: Record<SoulMapProfileFieldKey, string[]> = {
    language_protocol: ['language_protocol', 'user.language_protocol'],
    guidance_style: ['guidance_style', 'user.guidance_style'],
    birth_date: [
      'birth_date',
      'user.birth_date',
      'date_of_birth',
      'user.date_of_birth',
    ],
    birth_time: ['birth_time', 'user.birth_time'],
    birth_place: [
      'birth_place',
      'user.birth_place',
      'birth_city',
      'user.birth_city',
      'city_of_birth',
      'user.city_of_birth',
    ],
    birth_lat: ['birth_lat', 'user.birth_lat'],
    birth_long: ['birth_long', 'user.birth_long'],
  };

  const names = new Set(aliases[fieldKey]);
  return variables?.find((variable) => names.has(variable.name));
}

function buildSoulMapProfileFieldPayload(
  fieldKey: SoulMapProfileFieldKey,
  form: SoulMapProfileForm,
  variable: MyVariable | undefined
): UserVariableUpdatePayload {
  const value = getSoulMapProfileFormValue(fieldKey, form);
  const payloadKey = variable?.name ?? fieldKey;

  if (variable) {
    return buildUserVariableUpdatePayload([variable], {
      [variable.name]: value,
    });
  }

  return { [payloadKey]: value };
}

function getSoulMapProfileFormValue(
  fieldKey: SoulMapProfileFieldKey,
  form: SoulMapProfileForm
): string {
  switch (fieldKey) {
    case 'language_protocol':
      return form.languageProtocol;
    case 'guidance_style':
      return form.guidanceStyle;
    case 'birth_date':
      return formatBirthDateForApi(form.birthDate);
    case 'birth_time':
      return formatBirthTimeForApi(form.birthTime);
    case 'birth_place':
      return form.birthPlace
        ? normalizeBirthPlaceLabel(form.birthPlace.label)
        : '';
    case 'birth_lat':
      return form.birthPlace ? String(form.birthPlace.lat) : '';
    case 'birth_long':
      return form.birthPlace ? String(form.birthPlace.lng) : '';
  }
}

interface LegacySoulMapProfileDraft extends Partial<SoulMapProfileForm> {
  birthDateTime?: string;
  birthCity?: string;
}

export function readSoulMapProfileDraft(): LegacySoulMapProfileDraft | null {
  try {
    const raw = sessionStorage.getItem(SOULMAP_PROFILE_DRAFT_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as LegacySoulMapProfileDraft;
  } catch {
    return null;
  }
}

export function writeSoulMapProfileDraft(form: SoulMapProfileForm): void {
  sessionStorage.setItem(SOULMAP_PROFILE_DRAFT_KEY, JSON.stringify(form));
}

function findLegacyBirthDateTimeVariable(
  variables: MyVariable[] | undefined
): MyVariable | undefined {
  const legacyNames = new Set(['birth_date_time', 'user.birth_date_time']);
  return variables?.find((variable) => legacyNames.has(variable.name));
}

function resolveBirthFieldsFromLegacy(
  variables: MyVariable[] | undefined,
  draft: LegacySoulMapProfileDraft | null
): Pick<SoulMapProfileForm, 'birthDate' | 'birthTime' | 'birthPlace'> {
  const birthDateFromApi = toDateInputValue(
    findSoulMapProfileVariable(variables, 'birth_date')?.value
  );
  const birthTimeFromApi = toTimeInputValue(
    findSoulMapProfileVariable(variables, 'birth_time')?.value
  );
  const birthPlaceFromApi = resolveBirthPlaceFromVariables(variables, draft);

  let birthDate = birthDateFromApi || draft?.birthDate?.trim() || '';
  let birthTime = birthTimeFromApi || draft?.birthTime?.trim() || '';

  if (!birthDate || !birthTime) {
    const legacyDateTime =
      draft?.birthDateTime?.trim() ||
      toDateTimeLocalValue(findLegacyBirthDateTimeVariable(variables)?.value);

    if (
      legacyDateTime &&
      /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(legacyDateTime)
    ) {
      birthDate = birthDate || legacyDateTime.slice(0, 10);
      birthTime = birthTime || legacyDateTime.slice(11, 16);
    }
  }

  return {
    birthDate: formatBirthDateForApi(birthDate),
    birthTime: formatBirthTimeForApi(birthTime),
    birthPlace: birthPlaceFromApi ?? draft?.birthPlace ?? null,
  };
}

/** The label stored on the profile, even when it carries no coordinates. */
export function getStoredBirthPlaceLabel(variables?: MyVariable[]): string {
  return getBirthPlaceLabelFromVariable(
    findSoulMapProfileVariable(variables, 'birth_place')?.value
  );
}

/**
 * birth_place is a plain text variable, so a profile can hold a city name with
 * no usable birth_lat/birth_long beside it. The chart needs coordinates, so
 * look the label back up in the city catalog rather than making the user retype
 * it.
 *
 * The catalog scores against the city name alone, so a stored
 * "City, Region, Country" string matches nothing when passed in whole. Search
 * on the city and use the trailing parts to pick between same-named cities.
 */
export async function hydrateBirthPlaceLabel(
  label: string
): Promise<SoulMapBirthPlace | null> {
  const parts = normalizeBirthPlaceLabel(label)
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);

  const [cityName, ...rest] = parts;
  if (!cityName) return null;

  const matches = await searchCitiesInCatalog(cityName, 40);
  if (!matches.length) return null;

  if (!rest.length) {
    return cityRecordToBirthPlace(matches[0]);
  }

  // Stored values may use a country name ("India") or an ISO code ("IN").
  const countryHint = rest[rest.length - 1];
  const inCountry = matches.filter((city) =>
    cityMatchesCountryHint(city, countryHint)
  );
  if (!inCountry.length) return null;

  const regionHint = rest.length > 1 ? rest[0] : '';
  const withRegion = regionHint
    ? inCountry.find((city) => cityMatchesRegionHint(city, regionHint))
    : undefined;

  return cityRecordToBirthPlace(withRegion ?? inCountry[0]);
}

function cityMatchesCountryHint(city: CityRecord, hint: string): boolean {
  const token = hint.trim().toLowerCase();
  if (!token) return false;
  if (city.countryCode.toLowerCase() === token) return true;

  const label = formatCityLabel(city).toLowerCase();
  return label.endsWith(`, ${token}`);
}

function cityMatchesRegionHint(city: CityRecord, hint: string): boolean {
  const token = hint.trim().toLowerCase();
  if (!token) return false;
  if (city.admin1Code.toLowerCase() === token) return true;
  return formatCityLabel(city).toLowerCase().includes(token);
}

function resolveBirthPlaceFromVariables(
  variables: MyVariable[] | undefined,
  draft: LegacySoulMapProfileDraft | null
): SoulMapBirthPlace | null {
  const rawPlaceValue = findSoulMapProfileVariable(
    variables,
    'birth_place'
  )?.value;
  const birthPlaceFromJson = parseSoulMapBirthPlace(rawPlaceValue);
  if (birthPlaceFromJson) {
    return birthPlaceFromJson;
  }

  const lat =
    parseCoordinate(
      findSoulMapProfileVariable(variables, 'birth_lat')?.value
    ) ??
    draft?.birthPlace?.lat ??
    null;
  const lng =
    parseCoordinate(
      findSoulMapProfileVariable(variables, 'birth_long')?.value
    ) ??
    draft?.birthPlace?.lng ??
    null;

  const label =
    getBirthPlaceLabelFromVariable(rawPlaceValue) ||
    draft?.birthPlace?.label ||
    '';

  if (!label) {
    return draft?.birthPlace ?? null;
  }

  if (lat === null || lng === null) {
    return draft?.birthPlace?.label === label ? draft.birthPlace : null;
  }

  return {
    label,
    placeId: draft?.birthPlace?.placeId ?? '',
    lat,
    lng,
  };
}

/** @deprecated Legacy combined datetime helper for migration only. */
function toDateTimeLocalValue(value: unknown): string {
  const raw = normalizeVariableString(value);
  if (!raw) return '';
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(raw)) return raw;
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return '';
  const offsetMs = parsed.getTimezoneOffset() * 60_000;
  return new Date(parsed.getTime() - offsetMs).toISOString().slice(0, 16);
}

/**
 * `useDraft` replays the consumer landing's in-progress sessionStorage draft.
 * Confirmation surfaces pass false so they show the saved profile, not a draft
 * left behind by another Soul Map flow in the same tab.
 */
export function resolveSoulMapProfileForm(
  variables?: MyVariable[],
  options?: { useDraft?: boolean }
): SoulMapProfileForm {
  const draft = (options?.useDraft ?? true) ? readSoulMapProfileDraft() : null;
  const birthFields = resolveBirthFieldsFromLegacy(variables, draft);

  const languageFromApi = parseLanguageProtocolPreference(
    findSoulMapProfileVariable(variables, 'language_protocol')?.value
  );
  const guidanceFromApi = parseGuidanceStylePreference(
    findSoulMapProfileVariable(variables, 'guidance_style')?.value
  );

  return {
    languageProtocol:
      languageFromApi ??
      parseLanguageProtocolPreference(draft?.languageProtocol) ??
      EMPTY_SOULMAP_PROFILE_FORM.languageProtocol,
    guidanceStyle:
      guidanceFromApi ??
      parseGuidanceStylePreference(draft?.guidanceStyle) ??
      EMPTY_SOULMAP_PROFILE_FORM.guidanceStyle,
    ...birthFields,
  };
}

export function getMaxBirthDateInputValue(): string {
  return new Date().toISOString().slice(0, 10);
}

/** Birth variables the save endpoint can reject, mapped to their form field. */
const BIRTH_FIELD_BY_VARIABLE_NAME: Record<
  string,
  'birthDate' | 'birthTime' | 'birthPlace'
> = {
  birth_date: 'birthDate',
  birth_time: 'birthTime',
  birth_place: 'birthPlace',
  birth_lat: 'birthPlace',
  birth_long: 'birthPlace',
};

/**
 * The save endpoint answers a rejected birth detail with a 400 keyed by the
 * variable name, e.g. `{ "birth_place": ["Enter a birth city, ..."] }`. Map
 * those onto the form so the message lands under the field it concerns.
 * Anything else (network, 5xx, unrelated variables) yields no field errors.
 */
export function getSoulMapProfileFieldErrorsFromApi(error: unknown): {
  birthDate?: string;
  birthTime?: string;
  birthPlace?: string;
} {
  if (!isAxiosError(error) || error.response?.status !== 400) return {};

  const data: unknown = error.response.data;
  if (!data || typeof data !== 'object') return {};

  const errors: {
    birthDate?: string;
    birthTime?: string;
    birthPlace?: string;
  } = {};
  for (const [name, messages] of Object.entries(data)) {
    const field =
      BIRTH_FIELD_BY_VARIABLE_NAME[normalizeProfileVariableName(name)];
    const message = Array.isArray(messages) ? messages[0] : messages;
    if (field && !errors[field] && typeof message === 'string' && message) {
      errors[field] = message;
    }
  }
  return errors;
}

export function validateSoulMapProfileForm(form: SoulMapProfileForm): {
  birthDate?: string;
  birthTime?: string;
  birthPlace?: string;
} {
  const errors: {
    birthDate?: string;
    birthTime?: string;
    birthPlace?: string;
  } = {};

  if (!form.birthDate.trim()) {
    errors.birthDate = 'Birth date is required.';
  } else {
    const birthDate = formatBirthDateForApi(form.birthDate);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(birthDate)) {
      errors.birthDate = 'Enter a valid birth date.';
    } else {
      const date = new Date(`${birthDate}T12:00:00`);
      if (Number.isNaN(date.getTime())) {
        errors.birthDate = 'Enter a valid birth date.';
      } else {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        if (date >= today) {
          errors.birthDate = 'Birth date must be in the past.';
        } else if (date.getFullYear() < 1900) {
          errors.birthDate = 'Please enter a plausible birth date.';
        }
      }
    }
  }

  if (!form.birthTime.trim()) {
    errors.birthTime = 'Birth time is required.';
  } else {
    const birthTime = formatBirthTimeForApi(form.birthTime);
    if (!/^\d{2}:\d{2}$/.test(birthTime)) {
      errors.birthTime = 'Enter a valid birth time.';
    }
  }

  if (!form.birthPlace) {
    errors.birthPlace = 'Select your city of birth from the suggestions.';
  } else if (
    !Number.isFinite(form.birthPlace.lat) ||
    !Number.isFinite(form.birthPlace.lng)
  ) {
    errors.birthPlace = 'Select a city so we can locate it on the map.';
  }

  return errors;
}

export function buildSoulMapProfilePayload(
  form: SoulMapProfileForm,
  variables?: MyVariable[]
): UserVariableUpdatePayload {
  return SOULMAP_PROFILE_FIELD_KEYS.reduce<UserVariableUpdatePayload>(
    (payload, fieldKey) => {
      const variable = findSoulMapProfileVariable(variables, fieldKey);
      return {
        ...payload,
        ...buildSoulMapProfileFieldPayload(fieldKey, form, variable),
      };
    },
    {}
  );
}

function findBirthConfirmedVariable(
  variables?: MyVariable[]
): MyVariable | undefined {
  const names = new Set(BIRTH_CONFIRMED_VARIABLE_NAMES);
  return variables?.find((variable) => names.has(variable.name));
}

/**
 * Whether the user has already confirmed their birth details. Absent or
 * unparseable means not confirmed, so the modal asks rather than assumes.
 */
export function hasConfirmedSoulMapBirthDetails(
  variables?: MyVariable[]
): boolean {
  const raw = findBirthConfirmedVariable(variables)?.value;
  if (typeof raw === 'boolean') {
    return raw;
  }

  const normalized = normalizeVariableString(raw).toLowerCase();
  return normalized === 'true' || normalized === '1';
}

function buildBirthConfirmedPayload(
  variables?: MyVariable[]
): UserVariableUpdatePayload {
  const variable = findBirthConfirmedVariable(variables);
  if (
    variable &&
    (variable.name === BIRTH_CONFIRMED_WRITE_NAME ||
      variable.name === `user.${BIRTH_CONFIRMED_WRITE_NAME}`)
  ) {
    return buildUserVariableUpdatePayload([variable], {
      [variable.name]: 'true',
    });
  }

  return { [BIRTH_CONFIRMED_WRITE_NAME]: true };
}

export function isSoulMapProfileConfigured(variables?: MyVariable[]): boolean {
  return SOULMAP_PROFILE_FIELD_KEYS.every((fieldKey) =>
    Boolean(findSoulMapProfileVariable(variables, fieldKey))
  );
}

/** Persist profile fields so the agent and chart service can read them at session start. */
export async function saveSoulMapProfile(
  form: SoulMapProfileForm,
  variables?: MyVariable[],
  options?: { persistDraft?: boolean; markBirthConfirmed?: boolean }
): Promise<void> {
  const normalizedForm: SoulMapProfileForm = {
    ...form,
    birthDate: formatBirthDateForApi(form.birthDate),
    birthTime: formatBirthTimeForApi(form.birthTime),
    birthPlace: form.birthPlace
      ? {
          ...form.birthPlace,
          label: normalizeBirthPlaceLabel(form.birthPlace.label),
        }
      : null,
  };

  if (options?.persistDraft ?? true) {
    writeSoulMapProfileDraft(normalizedForm);
  }

  // One request, so the flag can never be set without the values landing too.
  await VariableService.updateUserVariable({
    ...buildSoulMapProfilePayload(normalizedForm, variables),
    ...(options?.markBirthConfirmed
      ? buildBirthConfirmedPayload(variables)
      : {}),
  });
}
